export type ByteRegionKind = 'empty' | 'text' | 'repeated' | 'structured' | 'high-entropy';

export interface ByteOverviewBlock {
  offset: number;
  length: number;
  entropy: number;
  printableRatio: number;
  nullRatio: number;
  kind: ByteRegionKind;
}

function blockEntropy(bytes: Uint8Array): number {
  const counts = new Uint32Array(256);
  for (const byte of bytes) counts[byte] += 1;
  let entropy = 0;
  for (const count of counts) {
    if (count === 0) continue;
    const probability = count / bytes.length;
    entropy -= probability * Math.log2(probability);
  }
  return entropy;
}

export function analyzeByteBlocks(bytes: Uint8Array, maxBlocks = 96): ByteOverviewBlock[] {
  if (bytes.length === 0) return [];
  const blockSize = Math.max(1, Math.ceil(bytes.length / maxBlocks));
  const blocks: ByteOverviewBlock[] = [];

  for (let offset = 0; offset < bytes.length; offset += blockSize) {
    const block = bytes.subarray(offset, Math.min(bytes.length, offset + blockSize));
    let printable = 0;
    let nulls = 0;
    const unique = new Set<number>();
    for (const byte of block) {
      if (byte >= 32 && byte <= 126) printable += 1;
      if (byte === 0) nulls += 1;
      unique.add(byte);
    }
    const entropy = blockEntropy(block);
    const printableRatio = printable / block.length;
    const nullRatio = nulls / block.length;
    let kind: ByteRegionKind = 'structured';
    if (nullRatio > 0.85) kind = 'empty';
    else if (printableRatio > 0.75) kind = 'text';
    else if (unique.size <= Math.max(2, block.length / 16)) kind = 'repeated';
    else if (entropy > 7.25) kind = 'high-entropy';
    blocks.push({ offset, length: block.length, entropy, printableRatio, nullRatio, kind });
  }

  return blocks;
}
